import mlApi from '@/api/ml';
import { combinationCount } from '@/helpers/fpReductionMetrics';
import { readSaveDraft, writeSaveDraft } from '@/helpers/fpReductionDrafts';

// Yanlış Pozitif Azaltma sihirbazı durumu. Adımlar: 1 Veri, 2 Hedef, 3 Öznitelikler, 4 Eğitim, 5 Sonuçlar, 6 Kaydet.
export const FP_STEPS = { SOURCE: 1, TARGET: 2, FEATURES: 3, TRAINING: 4, RESULTS: 5, SAVE: 6 };
export const FP_ROLES = { BASE: 'base', CANDIDATE: 'candidate', SKIP: 'skip' };
export const FP_MODES = { SINGLE: 'single', EXPERIMENT: 'experiment' };
export const FP_VALUE_TYPES = { NUMERIC: 'Numeric', CATEGORIC: 'Categoric', ORDINAL: 'Ordinal' };
export const FP_LABEL_ROLES = { POSITIVE: 'positive', NEGATIVE: 'negative', EXCLUDED: 'excluded' };
export const FP_SOURCE_MODES = { TABLE: 'Table', SQL: 'Sql' };
export const FP_IMBALANCE = { NONE: 'None', CLASS_WEIGHT: 'ClassWeight', OVERSAMPLING: 'Oversampling' };
export const FP_RESULT_VIEWS = { SINGLE: 'single', EXPERIMENT: 'experiment' };
export const FP_JOB_TYPES = { CLASSIFICATION: 0, FEATURE_EXPERIMENT: 5 };
// Backend MlExperiment varsayılanlarıyla aynı; sunucu yine de doğrular.
export const FP_MAX_CANDIDATES = 8;
export const FP_MAX_COMBINATIONS = 256;
export const FP_DEFAULT_THRESHOLD = 0.5;
// Backend MlTrainerFactory.SupportsWeights ile aynı liste.
export const FP_NO_WEIGHT_ALGORITHMS = ['AveragedPerceptron', 'FieldAwareFactorizationMachine'];

export function algorithmSupportsWeights(algorithm) {
  return !FP_NO_WEIGHT_ALGORITHMS.includes(algorithm);
}

const TARGET_NAME_PATTERN = /(DURUM|STATU|STATUS|KARAR|SONUC|SONUÇ|RESULT|LABEL|TARGET|HEDEF|DECISION)/i;
const NEGATIVE_VALUE_PATTERN = /(YANLIS|YANLIŞ|FALSE|^FP$|HAYIR)/i;
const POSITIVE_VALUE_PATTERN = /(GERCEK|GERÇEK|REAL|DETECTION|TESPIT|TESPİT|^RD$|^TRUE$|EVET)/i;
const MAX_TARGET_DISTINCT = 20;
const SKIP_FLAGS = ['IdLike', 'Constant', 'Date', 'HighCardinality'];

function initialTraining() {
  return {
    algorithm: 'FastTree',
    testPercentage: 25,
    imbalanceMethod: FP_IMBALANCE.CLASS_WEIGHT,
    positiveClassWeight: null,
    isNormalized: true,
    weightRules: [],
    rankingMetric: 'FpReduction',
  };
}

function initialSave() {
  return { modelName: '', modelDescription: '', executeSqlText: '', saving: false, savedModelName: '' };
}

function initialState() {
  return {
    step: FP_STEPS.SOURCE,
    dataSourceName: '',
    schemas: [],
    schemasLoading: false,
    tables: [],
    tablesLoading: false,
    source: { mode: FP_SOURCE_MODES.TABLE, schemaName: '', tableName: '', sqlText: '' },
    profile: null,
    profileLoading: false,
    targetColumn: '',
    distribution: null,
    distributionLoading: false,
    labelRoles: {},
    mode: FP_MODES.SINGLE,
    columns: {},
    training: initialTraining(),
    job: null,
    singleResult: null,
    singleJobId: null,
    experimentResult: null,
    resultView: FP_RESULT_VIEWS.SINGLE,
    selectedCombinationId: null,
    retrainCombinationId: null,
    threshold: FP_DEFAULT_THRESHOLD,
    selectedCell: null,
    save: initialSave(),
  };
}

function isSuggestedTarget(column) {
  return column.distinctCount >= 2 && column.distinctCount <= MAX_TARGET_DISTINCT && TARGET_NAME_PATTERN.test(column.name);
}

// Profilden güvenli varsayılanlar: ID benzeri, sabit, tarih ve çok fazla farklı değerli metin kolonları kullanılmaz;
// BOOLEAN tipi sunulmaz (skorlamada ikili kodlama ters dönebildiği için 0/1 kolonlar Sayısal kalır).
function defaultColumnConfig(column) {
  const skip = (column.flags || []).some(flag => SKIP_FLAGS.includes(flag));
  return {
    role: skip ? FP_ROLES.SKIP : FP_ROLES.BASE,
    valueType: column.detectedType === FP_VALUE_TYPES.NUMERIC ? FP_VALUE_TYPES.NUMERIC : FP_VALUE_TYPES.CATEGORIC,
    ordinalOrder: (column.topValues || []).map(item => item.value),
  };
}

function suggestLabelRole(value) {
  if (NEGATIVE_VALUE_PATTERN.test(value)) {
    return FP_LABEL_ROLES.NEGATIVE;
  }
  if (POSITIVE_VALUE_PATTERN.test(value)) {
    return FP_LABEL_ROLES.POSITIVE;
  }
  return FP_LABEL_ROLES.EXCLUDED;
}

function suggestLabelRoles(values) {
  const roles = {};
  values.forEach(item => {
    roles[item.value] = suggestLabelRole(item.value);
  });
  // Tek pozitif seçilebilir; birden çok aday varsa en sık olan kalır.
  const positives = values.filter(item => roles[item.value] === FP_LABEL_ROLES.POSITIVE);
  positives.slice(1).forEach(item => {
    roles[item.value] = FP_LABEL_ROLES.EXCLUDED;
  });
  // İki değerli hedefte tek taraf tanındıysa diğeri tamamlanır.
  if (values.length === 2) {
    const assigned = values.filter(item => roles[item.value] !== FP_LABEL_ROLES.EXCLUDED);
    if (assigned.length === 1) {
      const other = values.find(item => roles[item.value] === FP_LABEL_ROLES.EXCLUDED);
      roles[other.value] = roles[assigned[0].value] === FP_LABEL_ROLES.POSITIVE ? FP_LABEL_ROLES.NEGATIVE : FP_LABEL_ROLES.POSITIVE;
    }
  }
  return roles;
}

function isRuleComplete(rule) {
  return !!rule.column && !!rule.operator && rule.value !== null && rule.value !== undefined && `${rule.value}`.trim() !== ''
    && Number(rule.multiplier) >= 0.1 && Number(rule.multiplier) <= 100;
}

// Tamamlanmış bir işin ayar özetinden (result.config) eğitim isteği; öznitelikler istenen kolonlarla sınırlanır.
// positiveClassWeight, deneyde kullanılan (otomatik hesaplandıysa hesaplanan) değerdir.
function requestFromConfig(config, featureNames, splitKeyColumns) {
  return {
    source: { ...config.source },
    targetColumn: config.targetColumn,
    positiveLabel: config.positiveLabel,
    negativeLabels: config.negativeLabels || [],
    features: (config.features || []).filter(feature => featureNames.includes(feature.name)),
    splitKeyColumns: splitKeyColumns || config.splitKeyColumns || null,
    selectedModel: config.selectedModel,
    trainPercentage: config.testPercentage,
    imbalanceMethod: config.imbalanceMethod,
    positiveClassWeight: config.imbalanceMethod === FP_IMBALANCE.CLASS_WEIGHT ? config.positiveClassWeight : null,
    weightRules: config.weightRules || [],
    isNormalized: config.isNormalized,
  };
}

export default {
  namespaced: true,

  state: initialState,

  getters: {
    profileColumns: state => (state.profile ? state.profile.columns : []),
    featureCandidates: (state, getters) => getters.profileColumns.filter(column => column.name !== state.targetColumn),
    suggestedTargets: (state, getters) => getters.profileColumns.filter(isSuggestedTarget),
    positiveLabel: state => Object.keys(state.labelRoles).find(value => state.labelRoles[value] === FP_LABEL_ROLES.POSITIVE) || '',
    negativeLabels: state => Object.keys(state.labelRoles).filter(value => state.labelRoles[value] === FP_LABEL_ROLES.NEGATIVE),
    labelCounts: state => {
      const counts = { positive: 0, negative: 0, excluded: 0 };
      if (!state.distribution) {
        return counts;
      }
      state.distribution.values.forEach(item => {
        const role = state.labelRoles[item.value] || FP_LABEL_ROLES.EXCLUDED;
        counts[role] += item.count;
      });
      counts.excluded += state.distribution.nullCount || 0;
      return counts;
    },
    columnConfig: state => name => state.columns[name] || { role: FP_ROLES.SKIP, valueType: FP_VALUE_TYPES.CATEGORIC, ordinalOrder: [] },
    usedColumns: (state, getters) => getters.featureCandidates
      .filter(column => getters.columnConfig(column.name).role !== FP_ROLES.SKIP)
      .map(column => column.name),
    baseColumns: (state, getters) => (state.mode === FP_MODES.EXPERIMENT
      ? getters.usedColumns.filter(name => state.columns[name].role === FP_ROLES.BASE)
      : getters.usedColumns),
    candidateColumns: (state, getters) => (state.mode === FP_MODES.EXPERIMENT
      ? getters.usedColumns.filter(name => state.columns[name].role === FP_ROLES.CANDIDATE)
      : []),
    combinationTotal: (state, getters) => combinationCount(getters.baseColumns.length, getters.candidateColumns.length),
    usesWeights: state => state.training.imbalanceMethod === FP_IMBALANCE.CLASS_WEIGHT || state.training.weightRules.length > 0,
    weightsUnsupported: (state, getters) => !algorithmSupportsWeights(state.training.algorithm) && getters.usesWeights,
    // Adımda ilerlemeyi engelleyen ilk nedenin i18n anahtarı; boşsa adım geçerlidir.
    stepBlockReason: (state, getters) => step => {
      if (step === FP_STEPS.SOURCE) {
        return state.profile ? '' : 'machineLearning.fpWizard.reasons.loadData';
      }
      if (step === FP_STEPS.TARGET) {
        if (!state.targetColumn) return 'machineLearning.fpWizard.reasons.selectTarget';
        if (!getters.positiveLabel) return 'machineLearning.fpWizard.reasons.selectPositive';
        if (!getters.negativeLabels.length) return 'machineLearning.fpWizard.reasons.selectNegative';
        if (getters.labelCounts.positive < 2 || getters.labelCounts.negative < 2) return 'machineLearning.fpWizard.reasons.notEnoughRows';
        return '';
      }
      if (step === FP_STEPS.FEATURES) {
        if (!getters.usedColumns.length) return 'machineLearning.fpWizard.reasons.selectFeatures';
        if (state.mode === FP_MODES.EXPERIMENT) {
          if (!getters.candidateColumns.length) return 'machineLearning.fpWizard.reasons.selectCandidates';
          if (getters.candidateColumns.length > FP_MAX_CANDIDATES) return 'machineLearning.fpWizard.reasons.tooManyCandidates';
          if (getters.combinationTotal > FP_MAX_COMBINATIONS) return 'machineLearning.fpWizard.reasons.tooManyCombinations';
        }
        return '';
      }
      if (step === FP_STEPS.TRAINING) {
        if (getters.weightsUnsupported) return 'machineLearning.fpWizard.reasons.weightsUnsupported';
        if (state.training.weightRules.some(rule => !isRuleComplete(rule))) return 'machineLearning.fpWizard.reasons.incompleteRules';
        return '';
      }
      if (step === FP_STEPS.RESULTS) {
        return state.singleJobId && state.singleResult ? '' : 'machineLearning.fpWizard.reasons.trainFirst';
      }
      return '';
    },
    // Başlığı tıklanabilir en ileri adım. Sonuç ve kaydetme iş kaydına dayanır (kaydetme job sonucunu kullanır); bu yüzden
    // bildirimden dönüşte profil henüz yüklenmemiş olsa da sonuç/kaydet adımları açıktır. Aksi halde önceki adımlar geçerli olmalı.
    maxReachableStep: (state, getters) => {
      if (state.singleJobId && state.singleResult) {
        return FP_STEPS.SAVE;
      }
      if (state.experimentResult || state.job) {
        return FP_STEPS.RESULTS;
      }
      for (let step = FP_STEPS.SOURCE; step <= FP_STEPS.TRAINING; step++) {
        if (getters.stepBlockReason(step)) {
          return step;
        }
      }
      return FP_STEPS.TRAINING;
    },
    isJobRunning: state => !!state.job && (state.job.status === 'queued' || state.job.status === 'running'),
    selectedCombination: state => {
      if (!state.experimentResult || !state.selectedCombinationId) {
        return null;
      }
      return state.experimentResult.combinations.find(item => item.id === state.selectedCombinationId) || null;
    },
    trainRequest: (state, getters) => (featureNames, splitKeyColumns) => {
      const names = featureNames || getters.usedColumns;
      return {
        source: { ...state.source },
        targetColumn: state.targetColumn,
        positiveLabel: getters.positiveLabel,
        negativeLabels: getters.negativeLabels,
        features: names.map(name => {
          const config = getters.columnConfig(name);
          return {
            name,
            valueType: config.valueType,
            ordinalOrder: config.valueType === FP_VALUE_TYPES.ORDINAL ? config.ordinalOrder : null,
          };
        }),
        splitKeyColumns: splitKeyColumns || null,
        selectedModel: state.training.algorithm,
        trainPercentage: state.training.testPercentage,
        imbalanceMethod: state.training.imbalanceMethod,
        positiveClassWeight: state.training.imbalanceMethod === FP_IMBALANCE.CLASS_WEIGHT && state.training.positiveClassWeight
          ? Number(state.training.positiveClassWeight)
          : null,
        weightRules: state.training.weightRules.map(rule => ({
          column: rule.column,
          operator: rule.operator,
          value: `${rule.value}`,
          multiplier: Number(rule.multiplier),
        })),
        isNormalized: state.training.isNormalized,
      };
    },
    experimentRequest: (state, getters) => ({
      ...getters.trainRequest(getters.baseColumns.concat(getters.candidateColumns), null),
      baseColumns: getters.baseColumns,
      candidateColumns: getters.candidateColumns,
      rankingMetric: state.training.rankingMetric,
    }),
  },

  mutations: {
    SET(state, { key, value }) {
      state[key] = value;
    },
    PATCH_SOURCE(state, patch) {
      state.source = { ...state.source, ...patch };
    },
    PATCH_TRAINING(state, patch) {
      state.training = { ...state.training, ...patch };
    },
    PATCH_SAVE(state, patch) {
      state.save = { ...state.save, ...patch };
    },
    PATCH_JOB(state, patch) {
      state.job = patch ? { ...(state.job || {}), ...patch } : null;
    },
    SET_COLUMN(state, { name, patch }) {
      state.columns = { ...state.columns, [name]: { ...(state.columns[name] || {}), ...patch } };
    },
    SET_LABEL_ROLE(state, { value, role }) {
      const roles = { ...state.labelRoles };
      if (role === FP_LABEL_ROLES.POSITIVE) {
        Object.keys(roles).forEach(key => {
          if (roles[key] === FP_LABEL_ROLES.POSITIVE) {
            roles[key] = FP_LABEL_ROLES.EXCLUDED;
          }
        });
      }
      roles[value] = role;
      state.labelRoles = roles;
    },
    RESET_RESULTS(state) {
      state.job = null;
      state.singleResult = null;
      state.singleJobId = null;
      state.experimentResult = null;
      state.resultView = FP_RESULT_VIEWS.SINGLE;
      state.selectedCombinationId = null;
      state.retrainCombinationId = null;
      state.threshold = FP_DEFAULT_THRESHOLD;
      state.selectedCell = null;
      state.save = initialSave();
    },
    RESET_DATA(state) {
      state.profile = null;
      state.targetColumn = '';
      state.distribution = null;
      state.labelRoles = {};
      state.columns = {};
      state.training = { ...state.training, weightRules: [] };
    },
  },

  actions: {
    loadSchemas({ commit }) {
      commit('SET', { key: 'schemasLoading', value: true });
      return mlApi.fpReductionSchemas()
        .then(resp => {
          if (resp.data && resp.data.statusCode === 200 && resp.data.result) {
            commit('SET', { key: 'dataSourceName', value: resp.data.result.dataSourceName || '' });
            commit('SET', { key: 'schemas', value: resp.data.result.schemas || [] });
          }
        })
        .finally(() => {
          commit('SET', { key: 'schemasLoading', value: false });
        });
    },

    loadTables({ commit }, schema) {
      commit('SET', { key: 'tables', value: [] });
      if (!schema) {
        return Promise.resolve();
      }
      commit('SET', { key: 'tablesLoading', value: true });
      return mlApi.fpReductionTables(schema)
        .then(resp => {
          if (resp.data && resp.data.statusCode === 200 && resp.data.result) {
            commit('SET', { key: 'tables', value: resp.data.result });
          }
        })
        .finally(() => {
          commit('SET', { key: 'tablesLoading', value: false });
        });
    },

    // Kaynak değişince önceki profil, hedef ve sonuçlar geçersiz olur. İş sürerken kaynak ve hedef değiştirilemez
    // (arayüz de kilitlidir); aksi halde takibi süren işin sonucu yeni seçimlerin üzerine yazılırdı.
    patchSource({ commit, dispatch, state, getters }, patch) {
      if (getters.isJobRunning) {
        return Promise.resolve();
      }
      const schemaChanged = patch.schemaName !== undefined && patch.schemaName !== state.source.schemaName;
      commit('PATCH_SOURCE', schemaChanged ? { ...patch, tableName: '' } : patch);
      commit('RESET_DATA');
      commit('RESET_RESULTS');
      if (schemaChanged) {
        return dispatch('loadTables', patch.schemaName);
      }
      return Promise.resolve();
    },

    loadProfile({ commit, dispatch, state, getters }) {
      if (getters.isJobRunning) {
        return Promise.resolve();
      }
      commit('SET', { key: 'profileLoading', value: true });
      commit('RESET_DATA');
      commit('RESET_RESULTS');
      return mlApi.fpReductionProfile(state.source)
        .then(resp => {
          if (!(resp.data && resp.data.statusCode === 200 && resp.data.result)) {
            return Promise.reject(resp.data);
          }
          const profile = resp.data.result;
          commit('SET', { key: 'profile', value: profile });
          const columns = {};
          profile.columns.forEach(column => {
            columns[column.name] = defaultColumnConfig(column);
          });
          commit('SET', { key: 'columns', value: columns });
          const suggested = getters.suggestedTargets;
          if (suggested.length === 1) {
            return dispatch('setTarget', suggested[0].name);
          }
          return null;
        })
        .finally(() => {
          commit('SET', { key: 'profileLoading', value: false });
        });
    },

    setTarget({ commit, state, getters }, column) {
      if (getters.isJobRunning) {
        return Promise.resolve();
      }
      const previous = state.targetColumn && state.profile
        ? state.profile.columns.find(item => item.name === state.targetColumn)
        : null;
      if (previous) {
        commit('SET_COLUMN', { name: state.targetColumn, patch: defaultColumnConfig(previous) });
      }
      commit('SET', { key: 'targetColumn', value: column });
      commit('SET', { key: 'labelRoles', value: {} });
      commit('SET', { key: 'distribution', value: null });
      commit('RESET_RESULTS');
      if (!column) {
        return Promise.resolve();
      }
      commit('SET_COLUMN', { name: column, patch: { role: FP_ROLES.SKIP } });
      commit('SET', { key: 'distributionLoading', value: true });
      return mlApi.fpReductionValueDistribution(state.source, column)
        .then(resp => {
          if (resp.data && resp.data.statusCode === 200 && resp.data.result) {
            commit('SET', { key: 'distribution', value: resp.data.result });
            commit('SET', { key: 'labelRoles', value: suggestLabelRoles(resp.data.result.values) });
          }
        })
        .finally(() => {
          commit('SET', { key: 'distributionLoading', value: false });
        });
    },

    // Sıralı kolonun tüm değerleri (sunucu değer dağılımı sınırı kadar) yüklenir; mevcut sıra korunur, yeni değerler sona eklenir.
    loadOrdinalValues({ commit, state }, name) {
      return mlApi.fpReductionValueDistribution(state.source, name).then(resp => {
        if (!(resp.data && resp.data.statusCode === 200 && resp.data.result)) {
          return Promise.reject(resp.data);
        }
        const current = (state.columns[name] && state.columns[name].ordinalOrder) || [];
        const added = resp.data.result.values.map(item => item.value).filter(value => !current.includes(value));
        commit('SET_COLUMN', { name, patch: { ordinalOrder: current.concat(added) } });
        return resp.data.result;
      });
    },

    applyDefaultColumns({ commit, state, getters }) {
      getters.featureCandidates.forEach(column => {
        commit('SET_COLUMN', { name: column.name, patch: defaultColumnConfig(column) });
      });
      if (state.targetColumn) {
        commit('SET_COLUMN', { name: state.targetColumn, patch: { role: FP_ROLES.SKIP } });
      }
    },

    setAllRoles({ commit, getters }, role) {
      getters.featureCandidates.forEach(column => {
        commit('SET_COLUMN', { name: column.name, patch: { role } });
      });
    },

    // Aday rolü yalnız karşılaştırma modunda anlamlıdır; tek modele dönerken adaylar sabite çevrilir.
    setMode({ commit, state }, mode) {
      commit('SET', { key: 'mode', value: mode });
      if (mode === FP_MODES.SINGLE) {
        Object.keys(state.columns)
          .filter(name => state.columns[name].role === FP_ROLES.CANDIDATE)
          .forEach(name => commit('SET_COLUMN', { name, patch: { role: FP_ROLES.BASE } }));
      }
    },

    // config verilirse (karşılaştırmadan "bu kombinasyonla eğit") istek o deneyin ayarlarından kurulur; deneyden sonra
    // formda değiştirilen ayarlar sıralama tablosundaki satırın yeniden üretilmesini bozmaz.
    startSingleTraining({ commit, getters }, { featureNames, splitKeyColumns, combinationId, config } = {}) {
      const request = config
        ? requestFromConfig(config, featureNames, splitKeyColumns)
        : getters.trainRequest(featureNames, splitKeyColumns);
      return mlApi.trainClassification(request).then(resp => {
        if (!(resp.data && resp.data.statusCode === 200 && resp.data.result && resp.data.result.jobId)) {
          return Promise.reject(resp.data);
        }
        commit('SET', { key: 'singleResult', value: null });
        commit('SET', { key: 'singleJobId', value: null });
        commit('SET', { key: 'retrainCombinationId', value: combinationId || null });
        commit('SET', { key: 'save', value: initialSave() });
        commit('PATCH_JOB', null);
        commit('PATCH_JOB', { id: resp.data.result.jobId, kind: FP_RESULT_VIEWS.SINGLE, status: 'queued' });
        return resp.data.result.jobId;
      });
    },

    startExperiment({ commit, getters }) {
      return mlApi.startFeatureExperiment(getters.experimentRequest).then(resp => {
        if (!(resp.data && resp.data.statusCode === 200 && resp.data.result && resp.data.result.jobId)) {
          return Promise.reject(resp.data);
        }
        commit('RESET_RESULTS');
        commit('PATCH_JOB', { id: resp.data.result.jobId, kind: FP_RESULT_VIEWS.EXPERIMENT, status: 'queued' });
        return resp.data.result.jobId;
      });
    },

    cancelJob({ state }) {
      if (!state.job || !state.job.id) {
        return Promise.resolve();
      }
      return mlApi.cancelTrainJob(state.job.id);
    },

    applyProgress({ commit }, job) {
      commit('PATCH_JOB', {
        status: job.status === 1 ? 'running' : 'queued',
        progressCurrent: job.progressCurrent,
        progressTotal: job.progressTotal,
        progressMessage: job.progressMessage,
        cancelRequested: job.cancelRequested === 1,
      });
    },

    // Kaydet formu bu işin tarayıcıda saklanan taslağıyla dolar; sayfadan çıkıp dönüldüğünde yazılanlar kaybolmaz.
    applySingleResult({ commit }, job) {
      const draft = readSaveDraft(job.id) || {};
      commit('SET', { key: 'singleResult', value: job.result });
      commit('SET', { key: 'singleJobId', value: job.id });
      commit('SET', {
        key: 'save',
        value: {
          ...initialSave(),
          modelName: draft.modelName || '',
          modelDescription: draft.modelDescription || '',
          executeSqlText: draft.executeSqlText || '',
          savedModelName: draft.savedModelName || '',
        },
      });
      commit('SET', { key: 'resultView', value: FP_RESULT_VIEWS.SINGLE });
      commit('SET', { key: 'threshold', value: FP_DEFAULT_THRESHOLD });
      commit('SET', { key: 'selectedCell', value: null });
      commit('PATCH_JOB', { status: 'done' });
    },

    applyExperimentResult({ commit }, { job, cancelled }) {
      commit('SET', { key: 'experimentResult', value: job.result });
      commit('SET', { key: 'resultView', value: FP_RESULT_VIEWS.EXPERIMENT });
      commit('SET', { key: 'selectedCombinationId', value: job.result.bestCombinationId || null });
      commit('PATCH_JOB', { status: cancelled ? 'cancelled' : 'done' });
    },

    failJob({ commit }, message) {
      commit('PATCH_JOB', { status: 'failed', error: message });
    },

    // Kullanıcının iptal ettiği iş hata değildir; sonuç ekranı nötr bir iptal bilgisi gösterir.
    markJobCancelled({ commit }) {
      commit('PATCH_JOB', { status: 'cancelled' });
    },

    // Bildirimden (?trainJobId) gelişte sonucu ve özet ayarları geri yükler; profil ve hedef dağılımı rehydrateData ile gelir.
    restoreFromJob({ commit }, job) {
      const result = job.result || {};
      const config = result.config || {};
      commit('RESET_DATA');
      commit('RESET_RESULTS');
      if (config.source) {
        commit('PATCH_SOURCE', config.source);
      }
      commit('SET', { key: 'targetColumn', value: config.targetColumn || '' });
      const roles = {};
      if (config.positiveLabel) {
        roles[config.positiveLabel] = FP_LABEL_ROLES.POSITIVE;
      }
      (config.negativeLabels || []).forEach(label => {
        roles[label] = FP_LABEL_ROLES.NEGATIVE;
      });
      commit('SET', { key: 'labelRoles', value: roles });
      const candidates = result.candidateColumns || [];
      const columns = {};
      (config.features || []).forEach(feature => {
        columns[feature.name] = {
          role: candidates.includes(feature.name) ? FP_ROLES.CANDIDATE : FP_ROLES.BASE,
          valueType: feature.valueType,
          ordinalOrder: feature.ordinalOrder || [],
        };
      });
      commit('SET', { key: 'columns', value: columns });
      commit('SET', { key: 'mode', value: job.jobType === FP_JOB_TYPES.FEATURE_EXPERIMENT ? FP_MODES.EXPERIMENT : FP_MODES.SINGLE });
      commit('PATCH_TRAINING', {
        algorithm: config.selectedModel || initialTraining().algorithm,
        testPercentage: config.testPercentage || initialTraining().testPercentage,
        imbalanceMethod: config.imbalanceMethod || initialTraining().imbalanceMethod,
        positiveClassWeight: config.imbalanceMethod === FP_IMBALANCE.CLASS_WEIGHT && config.positiveClassWeight
          ? `${config.positiveClassWeight}`
          : null,
        isNormalized: !!config.isNormalized,
        weightRules: config.weightRules || [],
        rankingMetric: result.rankingMetric || initialTraining().rankingMetric,
      });
      commit('SET', { key: 'step', value: FP_STEPS.RESULTS });
    },

    // Geri yüklenen işin kaynağı için tablo listesi, profil ve hedef dağılımı arka planda yüklenir. loadProfile/setTarget'tan
    // farklı olarak sıfırlama yapılmaz: geri yüklenen kolon rolleri, etiketler ve sonuçlar korunur; işte olmayan kolonlar
    // "Kullanma" olarak eklenir.
    rehydrateData({ commit, dispatch, state }) {
      const tablesLoaded = state.source.mode === FP_SOURCE_MODES.TABLE && state.source.schemaName
        ? dispatch('loadTables', state.source.schemaName)
        : Promise.resolve();
      commit('SET', { key: 'profileLoading', value: true });
      return tablesLoaded
        .then(() => mlApi.fpReductionProfile(state.source))
        .then(resp => {
          if (!(resp.data && resp.data.statusCode === 200 && resp.data.result)) {
            return Promise.reject(resp.data);
          }
          const profile = resp.data.result;
          const columns = {};
          profile.columns.forEach(column => {
            columns[column.name] = state.columns[column.name] || { ...defaultColumnConfig(column), role: FP_ROLES.SKIP };
          });
          commit('SET', { key: 'profile', value: profile });
          commit('SET', { key: 'columns', value: columns });
          if (!state.targetColumn) {
            return null;
          }
          commit('SET', { key: 'distributionLoading', value: true });
          return mlApi.fpReductionValueDistribution(state.source, state.targetColumn)
            .then(distributionResp => {
              if (distributionResp.data && distributionResp.data.statusCode === 200 && distributionResp.data.result) {
                commit('SET', { key: 'distribution', value: distributionResp.data.result });
              }
            })
            .finally(() => {
              commit('SET', { key: 'distributionLoading', value: false });
            });
        })
        .finally(() => {
          commit('SET', { key: 'profileLoading', value: false });
        });
    },

    // Forma yazılanlar işin taslağına da yazılır (bkz. applySingleResult).
    // Sihirbazı başa alır: kaynak, profil, hedef, kolonlar ve sonuçlar temizlenir; şema listesi korunur.
    resetWizard({ commit }) {
      commit('RESET_RESULTS');
      commit('RESET_DATA');
      commit('SET', { key: 'source', value: { mode: FP_SOURCE_MODES.TABLE, schemaName: '', tableName: '', sqlText: '' } });
      commit('SET', { key: 'tables', value: [] });
      commit('SET', { key: 'mode', value: FP_MODES.SINGLE });
      commit('SET', { key: 'training', value: initialTraining() });
      commit('SET', { key: 'step', value: FP_STEPS.SOURCE });
    },

    patchSaveDraft({ commit, state }, patch) {
      commit('PATCH_SAVE', patch);
      writeSaveDraft(state.singleJobId, {
        modelName: state.save.modelName,
        modelDescription: state.save.modelDescription,
        executeSqlText: state.save.executeSqlText,
      });
    },

    saveModel({ commit, state }) {
      commit('PATCH_SAVE', { saving: true });
      return mlApi.saveClassificationModel({
        trainJobId: state.singleJobId,
        modelName: state.save.modelName,
        modelDescription: state.save.modelDescription,
        executeSqlText: state.save.executeSqlText,
      })
        .then(resp => {
          if (!(resp.data && resp.data.statusCode === 200)) {
            return Promise.reject(resp.data);
          }
          commit('PATCH_SAVE', { savedModelName: state.save.modelName });
          writeSaveDraft(state.singleJobId, { savedModelName: state.save.modelName });
          return resp.data;
        })
        .finally(() => {
          commit('PATCH_SAVE', { saving: false });
        });
    },

  },
};
